import { newIdempotencyKey } from '@micarahr/api-client';
import { apiTransport } from './api-client';
import type { TimesheetRow } from '@micarahr/shared-types';
import type { Page } from './collections';
import type { AdminAttendanceRecord, AdminCorrectionRequest } from './seed';
import type { AuditActor } from './audit';
import { apiAttendanceAdminService } from './attendance-admin.api';
import { narrow, REQUEST_STATUSES } from './narrow';
import { decideRequest, type RequestDecision } from './request-decisions.api';
import { cursorPage } from './cursor-page';
import { correctionReasonLabel } from '../lib/labels';
import { todayInBusiness } from '../lib/business-date';

/**
 * Timesheets and attendance corrections.
 *
 * These two belong together because they are the same argument seen twice: an
 * attendance record is evidence, and everything downstream either accepts it or
 * records a decision beside it. Neither ever edits it.
 *
 * The timesheet period is the gate between attendance and payroll. Its value is
 * entirely in refusing — it will not lock while a timesheet is blocked by
 * unresolved attendance, because locking hands payroll hours nobody stood
 * behind and payroll will not ask again.
 */

interface ApiTimesheetPeriod {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  lockedAt: string | null;
  lockedByUserId: string | null;
  unlockedAt: string | null;
  unlockReason: string | null;
  unlockCount: number;
  createdAt: string;
}

interface ApiEmployeeTimesheet {
  id: string;
  periodId: string;
  employeeId: string;
  employee: {
    id: string;
    fullName: string;
    employeeNumber: string;
    department?: { name: string } | null;
  } | null;
  readiness: string;
  scheduledMinutes: number;
  workedMinutes: number;
  lateMinutes: number;
  undertimeMinutes: number;
  overtimeMinutes: number;
  leaveDays: string | number;
  missingLogCount: number;
  adjustmentCount: number;
  blockers: string[];
  lockedAt: string | null;
}

export const apiTimesheetService = {
  listPeriods(): Promise<ApiTimesheetPeriod[]> {
    return apiTransport().get<ApiTimesheetPeriod[]>('/api/v1/timesheet-periods');
  },

  createPeriod(input: { label: string; startDate: string; endDate: string }) {
    return apiTransport().post<ApiTimesheetPeriod>('/api/v1/timesheet-periods', input, {
      idempotencyKey: newIdempotencyKey(),
    });
  },

  /**
   * Build the period's timesheets from attendance.
   *
   * Refused on a locked period. Regenerating under a lock would silently
   * replace the figures payroll was handed, after somebody signed for them.
   */
  generate(periodId: string) {
    return apiTransport().post<{ ready: number; blocked: number }>(
      `/api/v1/timesheet-periods/${periodId}/generate`,
      {},
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  async listTimesheets(
    filter: { periodId?: string; readiness?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<ApiEmployeeTimesheet>> {
    const pageSize = filter.pageSize ?? 50;

    const page = await apiTransport().page<ApiEmployeeTimesheet>('/api/v1/timesheets', {
      query: {
        periodId: filter.periodId,
        readiness: filter.readiness === 'all' ? undefined : filter.readiness,
        limit: pageSize,
      },
    });

    return { rows: page.items, total: page.totalCount, page: filter.page ?? 1, pageSize };
  },

  /**
   * Lock the period.
   *
   * The server refuses while any timesheet is blocked and says how many. That
   * refusal is the entire point of the module, so the adapter does not
   * pre-check it — a stale client-side count would either block a valid lock or
   * permit an invalid one, and both are worse than the server's answer.
   */
  lock(periodId: string) {
    return apiTransport().post(
      `/api/v1/timesheet-periods/${periodId}/lock`,
      {},
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  /**
   * Unlock, with a reason.
   *
   * The reason is required and is kept on the period, not only in the audit
   * log: a period that was locked, unlocked and edited must not read like one
   * nobody ever locked.
   */
  unlock(periodId: string, reason: string) {
    return apiTransport().post(`/api/v1/timesheet-periods/${periodId}/unlock`, { reason });
  },
};

interface ApiCorrectionRequest {
  id: string;
  referenceCode: string;
  employeeId: string;
  employee: {
    id: string;
    fullName: string;
    employeeNumber: string;
    department: { id: string; name: string } | null;
  } | null;
  attendanceRecordId: string | null;
  date: string;
  reason: string;
  reasonLabel?: string | null;
  explanation: string;
  /*
   * The server's names. This adapter read `originalTimeIn`, which was never
   * sent, so "Recorded time in" showed a dash beside a record that had one.
   */
  currentTimeIn: string | null;
  currentTimeOut: string | null;
  requestedTimeIn: string | null;
  requestedTimeOut: string | null;
  status: string;
  decisionComment: string | null;
  decidedAt: string | null;
  decidedByUser?: { displayName: string } | null;
  createdAt: string;
}

function toCorrectionRequest(row: ApiCorrectionRequest): AdminCorrectionRequest {
  return {
    id: row.id,
    referenceCode: row.referenceCode,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    departmentId: row.employee?.department?.id ?? '',
    departmentName: row.employee?.department?.name ?? '',
    // Not resolved by this endpoint; the inspector fetches the employee when a
    // reviewer opens one. Null is honest, an empty string would read as
    // "no manager".
    managerName: null,
    attendanceRecordId: row.attendanceRecordId ?? '',
    date: row.date.slice(0, 10),
    reasonCode: row.reason,
    // The label stored with the request is the one the employee picked; the
    // shared vocabulary covers rows written before it was stored.
    reasonLabel: row.reasonLabel || correctionReasonLabel(row.reason),
    explanation: row.explanation,
    originalTimeIn: row.currentTimeIn,
    originalTimeOut: row.currentTimeOut,
    requestedTimeIn: row.requestedTimeIn,
    requestedTimeOut: row.requestedTimeOut,
    status: narrow(REQUEST_STATUSES, row.status, 'correction request status'),
    submittedAt: row.createdAt,
    decidedAt: row.decidedAt ?? null,
    decidedByName: row.decidedByUser?.displayName ?? null,
    hrComment: row.decisionComment ?? null,
    // Supporting files hang off the request as media records fetched with the
    // detail, so a list row has no name to give.
    attachmentName: null,
  };
}

export const apiCorrectionAdminService = {
  async list(
    filter: { status?: string; search?: string; page?: number; pageSize?: number } = {},
  ): Promise<Page<AdminCorrectionRequest>> {
    return cursorPage<ApiCorrectionRequest, AdminCorrectionRequest>(
      '/api/v1/attendance-corrections',
      {
        status: filter.status === 'all' ? undefined : filter.status,
        search: filter.search?.trim() || undefined,
      },
      filter,
      toCorrectionRequest,
    );
  },

  /** What the queue and the Approvals centre call; see `decideRequest`. */
  decide(id: string, decision: RequestDecision, comment: string): Promise<unknown> {
    return decideRequest('attendance-corrections', id, decision, comment);
  },

  /**
   * Decide a step.
   *
   * Explicit, like leave and overtime: the manager step and the HR step need
   * different permissions, and inferring one from the viewer's role picks
   * arbitrarily for somebody holding both.
   */
  approve(id: string, step: 'manager' | 'hr', comment: string) {
    return apiTransport().post(
      `/api/v1/attendance-corrections/${id}/approve-${step}`,
      { comment },
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  reject(id: string, comment: string) {
    return apiTransport().post(
      `/api/v1/attendance-corrections/${id}/reject`,
      { comment },
      { idempotencyKey: newIdempotencyKey() },
    );
  },
};

interface ApiEmployeeTimesheet {
  id: string;
  periodId: string;
  employeeId: string;
  employee: {
    id: string;
    fullName: string;
    employeeNumber: string;
    department?: { name: string } | null;
  } | null;
  period: { label: string; startDate: string; endDate: string } | null;
  scheduledMinutes: number;
  workedMinutes: number;
  lateMinutes: number;
  undertimeMinutes: number;
  overtimeMinutes: number;
  leaveDays: string | number;
  missingLogCount: number;
  reviewPendingCount?: number;
  inProgressDayCount?: number;
  adjustmentCount: number;
  readiness: string;
  lockedAt: string | null;
}

function toTimesheetRow(row: ApiEmployeeTimesheet): TimesheetRow {
  return {
    id: row.id,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    employeeNumber: row.employee?.employeeNumber ?? '',
    departmentName: row.employee?.department?.name ?? '',
    managerName: null,
    periodId: row.periodId,
    scheduledHours: Math.round((row.scheduledMinutes / 60) * 100) / 100,
    actualHours: Math.round((row.workedMinutes / 60) * 100) / 100,
    lateMinutes: row.lateMinutes,
    undertimeMinutes: row.undertimeMinutes,
    overtimeHours: Math.round((row.overtimeMinutes / 60) * 100) / 100,
    leaveDays: Number(row.leaveDays),
    missingLogCount: row.missingLogCount,
    reviewPendingCount: row.reviewPendingCount ?? 0,
    inProgressDayCount: row.inProgressDayCount ?? 0,
    adjustmentCount: row.adjustmentCount,
    readiness: row.readiness as TimesheetRow['readiness'],
    lockedAt: row.lockedAt,
  };
}

/**
 * The list and the summary the page actually renders.
 *
 * Both were missing from this adapter, so the selector's spread quietly kept
 * the prototype's — and the screen showed 119 fictional employees above a
 * company of 41 real ones, under the API badge. The same failure as the
 * organization headcount and the employee rail: a spread cannot tell an
 * unimplemented method from an implemented one, which is why every adapter
 * method that exists on the mock has to exist here or be knowingly absent.
 */
/** A timesheet period as the page's period picker needs it. */
export interface TimesheetPeriodOption {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  locked: boolean;
}

/**
 * The period a page should open on: the one today falls in, or failing that
 * the latest. Periods arrive newest first.
 */
export function currentPeriod<T extends { startDate: string; endDate: string }>(
  periods: T[],
  today = todayInBusiness(),
): T | null {
  return (
    periods.find((period) => period.startDate <= today && today <= period.endDate) ??
    periods[0] ??
    null
  );
}

async function fetchPeriods(): Promise<TimesheetPeriodOption[]> {
  const rows = await apiTransport().get<ApiTimesheetPeriod[]>('/api/v1/timesheet-periods');
  return rows.map((row) => ({
    id: row.id,
    label: row.label,
    startDate: row.startDate.slice(0, 10),
    endDate: row.endDate.slice(0, 10),
    locked: row.lockedAt !== null,
  }));
}

/*
 * Timesheets, one period at a time.
 *
 * GET /timesheets without a period answers every period's rows together, and
 * the page listed each employee once per period — three times over, under
 * totals that summed all three. Every read here names its period; with none
 * given it is the current one, so a caller cannot get the mixture by omission.
 */
async function resolvePeriodId(periodId: string | undefined): Promise<string | null> {
  if (periodId) return periodId;
  return currentPeriod(await fetchPeriods())?.id ?? null;
}

export const apiTimesheetListService = {
  periods: fetchPeriods,

  async list(
    filter: {
      readiness?: string;
      periodId?: string;
      search?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ): Promise<{ rows: TimesheetRow[]; total: number; page: number; pageSize: number }> {
    const periodId = await resolvePeriodId(filter.periodId);
    const rows = periodId
      ? await apiTransport().get<ApiEmployeeTimesheet[]>('/api/v1/timesheets', {
          query: {
            periodId,
            readiness: filter.readiness === 'all' ? undefined : filter.readiness,
          },
        })
      : [];

    const pageSize = filter.pageSize ?? 25;
    const pageNumber = filter.page ?? 1;
    const needle = filter.search?.trim().toLowerCase();
    const mapped = rows
      .map(toTimesheetRow)
      .filter(
        (row) =>
          !needle ||
          `${row.employeeName} ${row.employeeNumber} ${row.departmentName}`
            .toLowerCase()
            .includes(needle),
      );

    return {
      rows: mapped.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
      total: mapped.length,
      page: pageNumber,
      pageSize,
    };
  },

  async summary(periodId?: string) {
    const periods = await fetchPeriods();
    const period = periodId
      ? (periods.find((entry) => entry.id === periodId) ?? null)
      : currentPeriod(periods);
    const [rows, queue] = period
      ? await Promise.all([
          apiTransport().get<ApiEmployeeTimesheet[]>('/api/v1/timesheets', {
            query: { periodId: period.id },
          }),
          /*
           * The review queue itself, for the period's dates — the same request
           * Attendance Review makes, so the two pages cannot disagree about how
           * many days are waiting. The timesheets' own tally only covers people
           * with a timesheet in the period.
           */
          apiTransport().page<unknown>('/api/v1/attendance', {
            query: {
              flaggedOnly: true,
              from: period.startDate.slice(0, 10),
              to: period.endDate.slice(0, 10),
              limit: 1,
            },
          }),
        ])
      : [[], null];
    const needsReview = rows.filter((row) => row.readiness === 'needs_review').length;

    return {
      periodId: period?.id ?? null,
      periodLabel: period?.label ?? null,
      periodStart: period?.startDate ?? null,
      periodEnd: period?.endDate ?? null,
      total: rows.length,
      ready: rows.filter((row) => row.readiness === 'ready').length,
      needsReview,
      // Nothing wrong, but a scheduled day is not over yet: the server's own
      // readiness, so the tile and the "In progress" filter count the same rows.
      inProgress: rows.filter((row) => row.readiness === 'in_progress').length,
      blocked: rows.filter((row) => row.readiness === 'blocked').length,
      missingLogs: rows.reduce((total, row) => total + row.missingLogCount, 0),
      // Days in the period still in the review queue — the queue's own count.
      // Counted apart from missing logs, which is what they used to be
      // reported as.
      awaitingReviewDays:
        queue?.totalCount ?? rows.reduce((total, row) => total + (row.reviewPendingCount ?? 0), 0),
      // The "Needs review" figure, so the tile, the lock dialog and the
      // server's refusal all name the same timesheets.
      awaitingReviewTimesheets: needsReview,
      // The period's own lock, not "some row has a lockedAt" — a period whose
      // timesheets were never generated is not locked by having none.
      locked: period?.locked ?? false,
    };
  },

  /**
   * Lock the period (POST /timesheet-periods/:id/lock) and answer with how
   * many timesheets the lock covered — the server's count, not the number of
   * rows the page happened to have loaded. A blocked period is refused by the
   * server with its own message, which the page shows as it is.
   */
  /**
   * Rebuild the period's timesheets from attendance as it stands now. The
   * rows are a projection; after a correction or a review decision this is
   * how they catch up. The server also does it before every lock.
   */
  async regenerate(
    periodId: string,
    _actor: AuditActor,
  ): Promise<{ ready: number; blocked: number; needsReview: number; inProgress: number }> {
    return apiTransport().post<{
      ready: number;
      blocked: number;
      needsReview: number;
      inProgress: number;
    }>(
      `/api/v1/timesheet-periods/${periodId}/generate`,
      {},
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  async lockPeriod(periodId: string, _actor: AuditActor, expectedCount?: number): Promise<number> {
    const result = await apiTransport().post<{ locked: boolean; count?: number }>(
      `/api/v1/timesheet-periods/${periodId}/lock`,
      expectedCount === undefined ? {} : { expectedCount },
      { idempotencyKey: newIdempotencyKey() },
    );
    return result.count ?? 0;
  },

  /**
   * The days behind a timesheet total, from the attendance records the
   * server holds — the prototype read them from browser storage, which is
   * empty against the API, so every breakdown said "no records".
   */
  async daysFor(employeeId: string, from: string, to: string): Promise<AdminAttendanceRecord[]> {
    const page = await apiAttendanceAdminService.list({
      employeeId,
      from,
      to,
      page: 1,
      pageSize: 100,
    });
    return [...page.rows].sort((a, b) => a.date.localeCompare(b.date));
  },
};
